import { useState, useEffect } from 'react';
import { STOCK_DATABASE } from '../constants/stockDatabase';
import { getStocksList } from '../services/marketDataService';
const SYMBOLS_CACHE_KEY = 'investport_all_symbols';
const CACHE_VERSION_KEY = 'investport_symbols_version';
const CURRENT_VERSION = '2026-06-v3'; // Bumped: FMP stable /stock-list fix (no exchange field)

/**
 * useAllSymbols — Fetches the complete US stock symbol list from Finnhub ONCE,
 * then caches it permanently in localStorage.
 * Falls back to the static STOCK_DATABASE if the API hasn't been called yet.
 * 
 * Returns: { symbolMap: { AAPL: 'Apple Inc.', ... }, loading: boolean }
 */
export function useAllSymbols() {
  const [symbolMap, setSymbolMap] = useState(STOCK_DATABASE); // Start with static fallback
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // 1. Try loading from cache
    try {
      const cachedVersion = localStorage.getItem(CACHE_VERSION_KEY);
      const cached = localStorage.getItem(SYMBOLS_CACHE_KEY);
      if (cached && cachedVersion === CURRENT_VERSION) {
        const parsed = JSON.parse(cached);
        if (parsed && Object.keys(parsed).length > 1000) {
          // Merge: cached API data + static DB (static DB fills gaps for crypto etc.)
          setSymbolMap({ ...STOCK_DATABASE, ...parsed });
          return; // Already cached and up to date, no need to fetch
        }
      }
    } catch (e) {
      console.warn('Error reading symbol cache:', e);
    }

    // 2. Fetch from Finnhub (one-time)
    let isMounted = true;
    const fetchSymbols = async () => {
      setLoading(true);
      try {
        // Wait 3 seconds to let other hooks (prices, logos) finish first
        await new Promise(r => setTimeout(r, 3000));

        const response = await getStocksList('US');
        const data = response?.data || response;
        if (!Array.isArray(data) || data.length < 100) {
          console.warn('Unexpected symbols response, skipping cache');
          return;
        }

        // Build a clean symbol -> description map
        const apiMap = {};
        data.forEach(item => {
          if (
            item.symbol &&
            item.name &&
            !item.symbol.includes('.') // Skip symbols with dots (e.g. BRK.A)
          ) {
            // Capitalize description nicely
            const desc = item.name
              .split(' ')
              .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
              .join(' ');
            apiMap[item.symbol] = desc;
          }
        });

        if (isMounted && Object.keys(apiMap).length > 500) {
          // Merge with static DB (static DB has better names + crypto)
          const merged = { ...STOCK_DATABASE, ...apiMap };
          setSymbolMap(merged);

          // Persist to localStorage
          try {
            localStorage.setItem(SYMBOLS_CACHE_KEY, JSON.stringify(apiMap));
            localStorage.setItem(CACHE_VERSION_KEY, CURRENT_VERSION);
            console.log(`✅ Cached ${Object.keys(apiMap).length} US symbols from Twelve Data`);
          } catch (e) {
            console.warn('Failed to save symbols to localStorage:', e);
          }
        }
      } catch (e) {
        console.error('Error fetching symbols list:', e);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchSymbols();
    return () => { isMounted = false; };
  }, []);

  return { symbolMap, loadingSymbols: loading };
}
